/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect -- dette technique V1 (apostrophes FR + détection PWA via effets), à nettoyer post-lancement */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './button';

// Hook to detect if app can be installed
export function useInstallPrompt() {
    const [installPrompt, setInstallPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
            return;
        }

        // Check if iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // Listen for beforeinstallprompt event
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };

        // Listen for app installed event
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setInstallPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const promptInstall = async () => {
        if (!installPrompt) return false;
        
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        
        if (outcome === 'accepted') {
            setInstallPrompt(null);
            return true;
        }
        return false;
    };

    return { installPrompt, isInstalled, isIOS, promptInstall };
}

// Install banner component
export function InstallBanner() {
    const { installPrompt, isInstalled, isIOS, promptInstall } = useInstallPrompt();
    const [dismissed, setDismissed] = useState(false);
    const [showIOSInstructions, setShowIOSInstructions] = useState(false);

    // Check if user has previously dismissed
    useEffect(() => {
        const wasDismissed = localStorage.getItem('pwa-install-dismissed');
        if (wasDismissed) {
            const dismissedDate = new Date(wasDismissed);
            const hoursSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60);
            // Re-show after 24h — l'install PWA est essentielle pour les notifs natives
            if (hoursSinceDismissed < 24) {
                setDismissed(true);
            }
        }
    }, []);

    const handleDismiss = () => {
        setDismissed(true);
        localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    };

    const handleInstall = async () => {
        const installed = await promptInstall();
        if (installed) {
            setDismissed(true);
        }
    };

    // Don't show if already installed, dismissed, or no prompt available (and not iOS)
    if (isInstalled || dismissed || (!installPrompt && !isIOS)) {
        return null;
    }

    return (
        <>
            <AnimatePresence>
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
                >
                    <div className="bg-gradient-to-r from-[#681793] to-[#FF914D] p-[1px] rounded-2xl shadow-2xl shadow-[#681793]/30">
                        <div className="bg-card rounded-2xl p-4">
                            <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                                    <Smartphone className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-foreground text-sm">Installer Homly</h3>
                                    <p className="text-muted-foreground text-xs mt-0.5">
                                        Recevez vos <strong className="text-[#FF914D]">notifications urgentes</strong> directement sur votre écran d'accueil
                                    </p>
                                </div>
                                <button
                                    onClick={handleDismiss}
                                    className="p-1 hover:bg-muted rounded-lg transition-colors"
                                >
                                    <X className="w-4 h-4 text-muted-foreground" />
                                </button>
                            </div>
                            <div className="flex gap-2 mt-3">
                                {isIOS ? (
                                    <Button
                                        onClick={() => setShowIOSInstructions(true)}
                                        className="flex-1 h-9 text-sm bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Comment installer
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleInstall}
                                        className="flex-1 h-9 text-sm bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Installer
                                    </Button>
                                )}
                                <Button
                                    onClick={handleDismiss}
                                    variant="ghost"
                                    className="h-9 text-sm text-muted-foreground hover:text-white hover:bg-muted"
                                >
                                    Plus tard
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* iOS Instructions Modal */}
            <AnimatePresence>
                {showIOSInstructions && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                        onClick={() => setShowIOSInstructions(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-card rounded-2xl p-6 max-w-sm w-full border border-border"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-bold text-foreground mb-4">
                                Installer Homly
                            </h3>
                            
                            {/* iOS Instructions */}
                            <div className="mb-4">
                                <p className="text-xs font-bold text-foreground mb-3 flex items-center gap-2">
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                                    iPhone / iPad
                                </p>
                                <p className="text-xs text-muted-foreground mb-3">Ouvrez <strong className="text-foreground">homly.pro</strong> dans Safari, puis :</p>
                                <ol className="space-y-4 text-sm text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <span className="w-7 h-7 rounded-full bg-[#681793] flex items-center justify-center text-white text-xs font-bold shrink-0">1</span>
                                        <div>
                                            <p>Appuyez sur le bouton <strong className="text-foreground">Partager</strong></p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">(le carre avec la fleche vers le haut, en bas de l'ecran)</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="w-7 h-7 rounded-full bg-[#681793] flex items-center justify-center text-white text-xs font-bold shrink-0">2</span>
                                        <div>
                                            <p>Appuyez sur <strong className="text-foreground">"En voir plus"</strong></p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">(le bouton avec le chevron, a droite des icones Copier, Signets, etc.)</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="w-7 h-7 rounded-full bg-[#681793] flex items-center justify-center text-white text-xs font-bold shrink-0">3</span>
                                        <div>
                                            <p>Appuyez sur <strong className="text-foreground">"Sur l'ecran d'accueil"</strong></p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">(l'icone + dans un carre, dans la liste qui apparait)</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="w-7 h-7 rounded-full bg-[#681793] flex items-center justify-center text-white text-xs font-bold shrink-0">4</span>
                                        <div>
                                            <p>Appuyez sur <strong className="text-foreground">Ajouter</strong> en haut a droite</p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">Homly apparaitra sur votre ecran d'accueil comme une application</p>
                                        </div>
                                    </li>
                                </ol>
                            </div>

                            {/* Android Instructions */}
                            <div className="pt-4 border-t border-border">
                                <p className="text-xs font-bold text-foreground mb-2 flex items-center gap-2">
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#3DDC84"><path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/></svg>
                                    Sur Android (Chrome)
                                </p>
                                <ol className="space-y-3 text-sm text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <span className="w-6 h-6 rounded-full bg-[#FF914D] flex items-center justify-center text-white text-xs font-bold shrink-0">1</span>
                                        <span>Ouvrez <strong className="text-foreground">homly.pro</strong> dans Chrome</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="w-6 h-6 rounded-full bg-[#FF914D] flex items-center justify-center text-white text-xs font-bold shrink-0">2</span>
                                        <span>Appuyez sur les <strong className="text-foreground">3 points</strong> en haut a droite</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="w-6 h-6 rounded-full bg-[#FF914D] flex items-center justify-center text-white text-xs font-bold shrink-0">3</span>
                                        <span>Appuyez sur <strong className="text-foreground">"Installer l'application"</strong> ou "Ajouter a l'ecran d'accueil"</span>
                                    </li>
                                </ol>
                            </div>
                            <Button
                                onClick={() => setShowIOSInstructions(false)}
                                className="w-full mt-6 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold"
                            >
                                Compris !
                            </Button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

export default InstallBanner;
